// NavConfirmationContext.js
import React, { createContext, useContext, useState } from 'react'
import PropTypes from 'prop-types'
import { matchPath, useLocation, useNavigate } from 'react-router-dom'
import ConfirmNavigationModal from './ConfirmNavigationModal'

const NavConfirmationContext = createContext()

export const useNavConfirmation = () => useContext(NavConfirmationContext)

// Define route where confirmation modal will be activated
//TODO: Añadir todas las urls de formularios
const allowedConfirmationRoutes = [
  '/management/edit/*',
  '/management/services/new',
  '/management/outages/new',
  '/management/outages/multipleupdates',
  '/management/maintenance/new',
  '/management/maintenance/multipleupdates',
  '/management/notifications/new',
  '/management/notifications/:id/edit',
  '/management/countries/new',
  '/management/countries/:id',
  '/management/languages/new',
  '/management/languages/:id',
  '/management/typesofcommunication/new',
  '/management/typesofcommunication/:id',
  '/management/emailgroups/new',
  '/management/emailgroups/:id',
  '/management/senders/new',
  '/management/senders/:id',
  '/management/cclist/new',
  '/management/cclist/:id',
  '/management/shortcodes/new',
  '/management/shortcodes/:id',
  '/management/users/new',
  '/management/users/:id',
  '/management/servicedependencies/new',
  '/management/servicedependencies/:id',
  '/management/clusters/new',
  '/management/clusters/:id',
  '/management/supportinformation',
]

export const NavConfirmationProvider = ({ children }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const isProtectedRoute = allowedConfirmationRoutes.some((route) =>
    matchPath(route, location.pathname),
  )
  const [showConfirm, setShowConfirm] = useState(false)
  const [nextPath, setNextPath] = useState(null)

  //Show confirmation modal on exit
  const [isFormDirty, setIsFormDirty] = useState(false)

  const [saveFunction, setSaveFunction] = useState(null)
  const [isSavingBeforeLeaving, setIsSavingBeforeLeaving] = useState(null)

  const blockNavigation = (path, saveCallback = null, force = false) => {
    if (isProtectedRoute && isFormDirty && !force) {
      setNextPath(path)
      setSaveFunction(() => saveCallback)
      setShowConfirm(true)
    } else {
      navigate(path) // If path does not require confirmation, navigate directly
    }
  }

  const confirmNavigation = () => {
    setShowConfirm(false)
    setIsFormDirty(false)
    navigate(nextPath)
  }

  const cancelNavigation = () => {
    setShowConfirm(false)
    setNextPath(null)
  }

  /*const saveChangesAndLeave = async () => {
    if (saveFunction) {
      setIsSavingBeforeLeaving(true)
      saveFunction().then((isValid) => {
        setIsSavingBeforeLeaving(false)
        if (isValid) { // ✅ Solo navegar si el formulario es válido
          setShowConfirm(false)
          setIsFormDirty(false)
          navigate(nextPath)
        } else {
          setNextPath(null)
          setShowConfirm(false)
        }
      }).catch((error) => {
        console.error("❌ Error en saveFunction:", error)
        setIsSavingBeforeLeaving(false)
        setShowConfirm(false)
      })
    }
  }*/
  const saveChangesAndLeave = async () => {
    if (saveFunction) {
      setIsSavingBeforeLeaving(true)

      const isValid = await saveFunction()

      setIsSavingBeforeLeaving(false)

      if (isValid) {
        // Only navigate if valid form
        setShowConfirm(false)
        setIsFormDirty(false)
        navigate(nextPath)
      } else {
        setNextPath(null)
        setShowConfirm(false)
      }
    }
  }

  return (
    <NavConfirmationContext.Provider
      value={{
        showConfirm,
        confirmNavigation,
        cancelNavigation,
        saveChangesAndLeave,
        blockNavigation,
        isFormDirty,
        setIsFormDirty,
        saveFunction,
        setSaveFunction,
        isSavingBeforeLeaving,
      }} // Expose variables and functions to be used for other files
    >
      {children}
      {showConfirm && (
        <ConfirmNavigationModal
          onConfirm={confirmNavigation}
          onCancel={cancelNavigation}
          onSaveAndLeave={saveChangesAndLeave}
          isSavingBeforeLeaving={isSavingBeforeLeaving}
        />
      )}
    </NavConfirmationContext.Provider>
  )
}
NavConfirmationProvider.propTypes = {
  children: PropTypes.node.isRequired, // `children` is mandatory and can be any React node
}
